import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Button,
  Divider,
  Autocomplete,
  TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import './EmployeeFilters.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EmployeeFilters = ({
  employees,
  employeeWorkload,
  employeeServiceDaysMap = {},
  designationFilter,
  statusFilter,
  workloadFilter,
  serviceDayFilter,
  cityFilter,
  onDesignationChange,
  onStatusChange,
  onWorkloadChange,
  onServiceDayChange,
  onCityChange
}) => {
  const [expanded, setExpanded] = useState(false);

  // Get employee status based on workload
  const getEmployeeStatus = (employeeId) => {
    const workload = employeeWorkload[employeeId] || 0;
    if (workload > 0) return 'active';
    return 'available';
  };

  // Get workload level
  const getWorkloadLevel = (workload) => {
    if (workload === 0) return 'none';
    if (workload < 5) return 'light';
    if (workload < 15) return 'medium';
    return 'heavy';
  };

  // Extract unique designations
  const uniqueDesignations = [...new Set(employees.map(e => e.empObject?.empDesignation).filter(Boolean))].sort();

  // Extract unique cities
  const uniqueCities = [...new Set(employees.map(e => e.empObject?.empCity).filter(Boolean))].sort();

  // Count employees by designation
  const designationCounts = {};
  uniqueDesignations.forEach(designation => {
    designationCounts[designation] = employees.filter(e => e.empObject?.empDesignation === designation).length;
  });

  // Count employees by status
  const statusCounts = {
    active: employees.filter(e => getEmployeeStatus(e.id) === 'active').length,
    available: employees.filter(e => getEmployeeStatus(e.id) === 'available').length
  };

  // Count employees by workload
  const workloadCounts = {
    none: employees.filter(e => getWorkloadLevel(employeeWorkload[e.id] || 0) === 'none').length,
    light: employees.filter(e => getWorkloadLevel(employeeWorkload[e.id] || 0) === 'light').length,
    medium: employees.filter(e => getWorkloadLevel(employeeWorkload[e.id] || 0) === 'medium').length,
    heavy: employees.filter(e => getWorkloadLevel(employeeWorkload[e.id] || 0) === 'heavy').length
  };

  // Count employees by service day (derived from work orders via employeeServiceDaysMap)
  const serviceDayCounts = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = employees.filter(e => (employeeServiceDaysMap[e.id] || []).includes(day)).length;
    return acc;
  }, {});

  const handleDesignationToggle = (designation) => {
    const newFilter = designationFilter.includes(designation)
      ? designationFilter.filter(d => d !== designation)
      : [...designationFilter, designation];
    onDesignationChange(newFilter);
  };

  const handleStatusToggle = (status) => {
    const newFilter = statusFilter.includes(status)
      ? statusFilter.filter(s => s !== status)
      : [...statusFilter, status];
    onStatusChange(newFilter);
  };

  const handleWorkloadToggle = (level) => {
    const newFilter = workloadFilter.includes(level)
      ? workloadFilter.filter(w => w !== level)
      : [...workloadFilter, level];
    onWorkloadChange(newFilter);
  };

  const handleServiceDayToggle = (day) => {
    const newFilter = serviceDayFilter.includes(day)
      ? serviceDayFilter.filter(d => d !== day)
      : [...serviceDayFilter, day];
    onServiceDayChange(newFilter);
  };

  const handleClearAll = () => {
    onDesignationChange([]);
    onStatusChange([]);
    onWorkloadChange([]);
    onServiceDayChange([]);
    onCityChange([]);
  };

  const hasActiveFilters = designationFilter.length > 0 ||
                          statusFilter.length > 0 ||
                          workloadFilter.length > 0 ||
                          serviceDayFilter.length > 0 ||
                          cityFilter.length > 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          borderRadius: 2,
          '&:before': { display: 'none' },
          boxShadow: expanded ? 3 : 1,
          transition: 'all 0.3s ease'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
          sx={{
            backgroundColor: '#f5f5f5',
            borderRadius: expanded ? '8px 8px 0 0' : '8px',
            padding: { xs: '8px 16px', sm: '16px' },
            minHeight: { xs: '48px', sm: '64px' },
            '&:hover': {
              backgroundColor: '#e8e8e8'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: '100%', flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ color: '#003366', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Advanced Filters
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${designationFilter.length + statusFilter.length + workloadFilter.length + serviceDayFilter.length + cityFilter.length} filter(s) applied`}
                size="small"
                color="primary"
                sx={{ ml: 'auto', mr: { xs: 0, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            {/* Designation Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Designation
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                {uniqueDesignations.map(designation => (
                  <FormControlLabel
                    key={designation}
                    control={
                      <Checkbox
                        checked={designationFilter.includes(designation)}
                        onChange={() => handleDesignationToggle(designation)}
                        size={window.innerWidth < 600 ? 'small' : 'medium'}
                        sx={{ color: '#003366', '&.Mui-checked': { color: '#003366' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                        <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>{designation}</span>
                        <Chip label={designationCounts[designation]} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                      </Box>
                    }
                    sx={{ mr: { xs: 0.5, sm: 2 } }}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            {/* Status Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Status
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilter.includes('active')}
                      onChange={() => handleStatusToggle('active')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Active</span>
                      <Chip label={statusCounts.active} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilter.includes('available')}
                      onChange={() => handleStatusToggle('available')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#06B6D4', '&.Mui-checked': { color: '#06B6D4' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Available</span>
                      <Chip label={statusCounts.available} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
              </FormGroup>
            </Box>

            <Divider />

            {/* Workload Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Workload Level
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workloadFilter.includes('none')}
                      onChange={() => handleWorkloadToggle('none')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#9CA3AF', '&.Mui-checked': { color: '#9CA3AF' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>None</span>
                      <Chip label={workloadCounts.none} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workloadFilter.includes('light')}
                      onChange={() => handleWorkloadToggle('light')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Light</span>
                      <Chip label={workloadCounts.light} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workloadFilter.includes('medium')}
                      onChange={() => handleWorkloadToggle('medium')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#F59E0B', '&.Mui-checked': { color: '#F59E0B' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Medium</span>
                      <Chip label={workloadCounts.medium} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workloadFilter.includes('heavy')}
                      onChange={() => handleWorkloadToggle('heavy')}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{ color: '#EF4444', '&.Mui-checked': { color: '#EF4444' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                      <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>Heavy</span>
                      <Chip label={workloadCounts.heavy} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                    </Box>
                  }
                  sx={{ mr: { xs: 0.5, sm: 2 } }}
                />
              </FormGroup>
            </Box>

            <Divider />

            {/* Service Days Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Service Days
              </Typography>
              <FormGroup row sx={{ gap: { xs: 0, sm: 1 } }}>
                {DAYS_OF_WEEK.map(day => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={serviceDayFilter.includes(day)}
                        onChange={() => handleServiceDayToggle(day)}
                        size={window.innerWidth < 600 ? 'small' : 'medium'}
                        sx={{ color: '#003366', '&.Mui-checked': { color: '#003366' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                        <span style={{ fontSize: window.innerWidth < 600 ? '0.85rem' : '1rem' }}>{day}</span>
                        <Chip
                          label={serviceDayCounts[day] || 0}
                          size="small"
                          sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                        />
                      </Box>
                    }
                    sx={{ mr: { xs: 0.5, sm: 2 } }}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            {/* City Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#003366', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Cities
              </Typography>
              <Autocomplete
                multiple
                options={uniqueCities}
                value={cityFilter}
                onChange={(event, newValue) => onCityChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select cities..."
                    size="small"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color="primary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    />
                  ))
                }
                sx={{ minWidth: { xs: '100%', sm: 300 } }}
              />
            </Box>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                  onClick={handleClearAll}
                  size="small"
                  fullWidth={window.innerWidth < 600}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Clear All Filters
                </Button>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EmployeeFilters;
